import React, { FC, useState } from 'react';
import { Row } from 'antd';
import { Formik, Form as FormikForm } from 'formik';
import Logo from 'assets/logo/logo_full.png';
import InputField from 'shared/components/InputField';
import COButton from 'shared/components/COButton';
import styles from './LoginForm.module.scss';
import { Fields, initialValues, validationSchema } from './LoginFormConstants';
import { LoginFormProps } from 'enums/LoginFormEnums';
import AuthenticationService from 'services/AuthenticationService/authentication.service';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'routes/routeConstants/appRoutes';
import VerifyMFAOTP from '../VerifyMFAOTP';

interface LoginFormValues {
  email: string;
  password: string;
}

const LoginView: FC = () => {
  const { adminLogin, buttonLoading, verifyOTPForLogin } = AuthenticationService();
  const navigate = useNavigate();
  const [requiresOTP, setRequiresOTP] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [userPassword, setUserPassword] = useState('');

  const handleSignupRedirection = () => navigate(AppRoutes.ADMIN_INFO);

  const handleForgotPasswordRedirection = () => navigate(AppRoutes.FORGOT_PASS);

  const handleApiSubmission = async (values: LoginFormValues) => {
    try {
      const result = await adminLogin({
        email: values.email,
        password: values.password,
      });

      // Check if MFA is required
      if (result && result.requiresOTP) {
        setUserEmail(result.email);
        setUserPassword(values.password); // Store password temporarily for resend
        setRequiresOTP(true);
      }
    } catch (error) {
      // Error handling is done in AuthenticationService
    }
  };

  const handleOTPVerify = async (otp: number) => {
    try {
      await verifyOTPForLogin({
        email: userEmail,
        otp: otp,
      });
      // Clear stored password after successful verification
      setUserPassword('');
    } catch (error) {
      // Error handling is done in AuthenticationService
    }
  };

  const handleResendOTP = async () => {
    // Resend OTP by calling login again with stored credentials
    try {
      const result = await adminLogin({
        email: userEmail,
        password: userPassword,
      });
      // The login call will trigger OTP resend
    } catch (error) {
      // Error handling
    }
  };

  // Show OTP verification form if MFA is required
  if (requiresOTP) {
    return <VerifyMFAOTP email={userEmail} onVerify={handleOTPVerify} onResend={handleResendOTP} />;
  }

  return (
    <div className={styles.formContainer}>
      <Row className={styles.logoContainer}>
        <img
          src={Logo}
          alt={LoginFormProps.IMAGE_ALT}
          className={styles.logo}
        />
      </Row>
      <Row className={styles.headingContainer}>
        <div>
          <h1>{LoginFormProps.HEADING}</h1>
        </div>
        <div>
          <h2>
            {LoginFormProps.SUBHEADING}{' '}
            <span className={styles.spanText} onClick={handleSignupRedirection}>
              {LoginFormProps.SUBHEADING_SPAN}
            </span>
          </h2>
        </div>
      </Row>
      <div className={styles.fieldsContainer}>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={values => handleApiSubmission(values)}>
          {formik => (
            <FormikForm>
              {Fields.map((field, index) => {
                return (
                  <div key={index} className={styles.fieldContainer}>
                    <InputField
                      name={field.name}
                      placeholderstring={field.placeholder}
                      label={field.label}
                      password={field.password}
                    />
                    {field.helper && (
                      <div className={styles.helpercontainer}>
                        <span
                          className={styles.spanText}
                          onClick={handleForgotPasswordRedirection}>
                          {LoginFormProps.HELPER_TEXT}
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}

              <div className={styles.buttonContainer}>
                <COButton
                  submit
                  loading={buttonLoading}
                  disabled={!formik.isValid}>
                  {LoginFormProps.BUTTON_LABEL_SUBMIT}
                </COButton>
              </div>
            </FormikForm>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default LoginView;
